import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Minus, Plus, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PayPalButton from "@/components/checkout/PayPalButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const PLANS = [
  {
    lookup_key: "basic_monthly",
    name: "Basic Valet",
    price: "$20",
    unit: "/month",
    tagline: "Up to 1 bag per pickup night",
    desc: "Ideal for solo residents with light weekly waste.",
    features: ["1 bag per night", "Choice of AM or PM window", "Instant email confirmation", "Cancel anytime"],
  },
  {
    lookup_key: "standard_monthly",
    name: "Standard Valet",
    price: "$30",
    unit: "/month",
    tagline: "Up to 3 bags per pickup night",
    desc: "Our most popular plan for full household convenience.",
    featured: true,
    features: ["3 bags per night", "Choice of AM or PM window", "Priority hallway sweep", "Instant email confirmation", "Cancel anytime"],
  },
  {
    lookup_key: "ondemand_bag",
    name: "On-Demand Bag",
    price: "$4",
    unit: "/bag",
    tagline: "Pay-as-you-go, no commitment",
    desc: "Flexible pickup for occasional bulk cleanup.",
    features: ["Per-bag pricing", "Same windows apply", "No subscription needed", "Instant email confirmation"],
  },
];

function CheckoutModal({ plan, open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", unit_number: "", pickup_window: "" });
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(false);

  if (!plan) return null;
  const isOnDemand = plan.lookup_key === "ondemand_bag";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.unit_number || !form.pickup_window) {
      toast.error("Please complete every field — name, email, unit number, and pickup window.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: plan.lookup_key,
        quantity: isOnDemand ? qty : 1,
        name: form.name,
        email: form.email,
        unit_number: form.unit_number,
        pickup_window: form.pickup_window,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-ink border-white/10 text-parchment sm:max-w-md" data-testid="checkout-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl" data-testid="checkout-modal-title">
            {plan.name} — {plan.price}{plan.unit}
          </DialogTitle>
          <DialogDescription className="text-mist text-sm">
            Your unit number and pickup window ride along with your payment.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label htmlFor="co-name" className="text-xs text-mist">Resident full name *</Label>
            <Input id="co-name" data-testid="checkout-name-input" value={form.name} placeholder="Alex Rivera"
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1.5 bg-white/5 border-white/10 text-parchment placeholder:text-dim" required />
          </div>
          <div>
            <Label htmlFor="co-email" className="text-xs text-mist">Email for confirmation *</Label>
            <Input id="co-email" type="email" data-testid="checkout-email-input" value={form.email} placeholder="alex@email.com"
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1.5 bg-white/5 border-white/10 text-parchment placeholder:text-dim" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="co-unit" className="text-xs text-mist">Unit / Apt number *</Label>
              <Input id="co-unit" data-testid="checkout-unit-input" value={form.unit_number} placeholder="402"
                onChange={(e) => setForm({ ...form, unit_number: e.target.value })}
                className="mt-1.5 bg-white/5 border-white/10 text-parchment placeholder:text-dim" required />
            </div>
            <div>
              <Label className="text-xs text-mist">Pickup window *</Label>
              <Select value={form.pickup_window} onValueChange={(v) => setForm({ ...form, pickup_window: v })} required>
                <SelectTrigger data-testid="checkout-window-select" className="mt-1.5 bg-white/5 border-white/10 text-parchment">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="bg-ink border-white/10 text-parchment">
                  <SelectItem value="morning" data-testid="checkout-window-morning">6:00 AM – 9:00 AM</SelectItem>
                  <SelectItem value="evening" data-testid="checkout-window-evening">6:00 PM – 9:00 PM</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {isOnDemand && (
            <div>
              <Label className="text-xs text-mist">Number of bags</Label>
              <div className="mt-1.5 flex items-center gap-4 bg-white/5 border border-white/10 rounded-md px-3 py-2 w-fit">
                <button type="button" data-testid="checkout-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))}
                  className="p-1 text-mist hover:text-parchment"><Minus className="w-4 h-4" /></button>
                <span className="font-display font-bold w-6 text-center" data-testid="checkout-qty-value">{qty}</span>
                <button type="button" data-testid="checkout-qty-plus" onClick={() => setQty(Math.min(25, qty + 1))}
                  className="p-1 text-mist hover:text-parchment"><Plus className="w-4 h-4" /></button>
                <span className="text-xs text-dim ml-2">= ${(qty * 4).toFixed(2)}</span>
              </div>
            </div>
          )}
          <button type="submit" data-testid="checkout-submit-button" disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-terracotta hover:bg-parchment hover:text-charcoal text-parchment font-semibold text-sm py-3.5 rounded-full transition-colors duration-300 disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            {loading ? "Opening secure checkout…" : "Continue to Secure Checkout"}
          </button>
          {isOnDemand && (
            <PayPalButton
              quantity={qty}
              getPayload={() => {
                if (!form.name || !form.email || !form.unit_number || !form.pickup_window) {
                  toast.error("Complete name, email, unit number, and pickup window before paying with PayPal.");
                  return null;
                }
                return { name: form.name, email: form.email, unit_number: form.unit_number, pickup_window: form.pickup_window };
              }}
            />
          )}
          <p className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-dim">
            Stripe test mode · Card 4242 4242 4242 4242
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Pricing() {
  const [selected, setSelected] = useState(null);

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-28 sm:py-36">
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[40rem] h-[40rem] rounded-full bg-forest/30 blur-[160px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6">
        <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }} className="font-mono text-xs uppercase tracking-[0.3em] text-terracotta mb-5">
          Pricing & services
        </motion.p>
        <motion.h2 initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl max-w-xl leading-tight">
          Simple tiers. <span className="text-stroke">Zero contracts.</span>
        </motion.h2>

        <div className="mt-16 grid md:grid-cols-3 gap-5 items-stretch">
          {PLANS.map((p, i) => (
            <motion.div
              key={p.lookup_key}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.12 }}
              data-testid={`plan-card-${p.lookup_key}`}
              className={`relative rounded-3xl p-8 flex flex-col border transition-colors duration-500 ${
                p.featured
                  ? "bg-forest/40 border-fern shadow-2xl shadow-forest/40 md:-my-4 md:py-12"
                  : "bg-white/[0.03] border-white/8 hover:border-terracotta/40"
              }`}
            >
              {p.featured && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-terracotta text-charcoal font-mono text-[10px] font-semibold uppercase tracking-[0.2em] px-4 py-1.5 rounded-full" data-testid="plan-featured-badge">
                  Most popular
                </span>
              )}
              <h3 className="font-display font-bold text-xl">{p.name}</h3>
              <p className="mt-1 text-xs font-mono uppercase tracking-[0.15em] text-mist">{p.tagline}</p>
              <div className="mt-8 flex items-baseline gap-1">
                <span className="font-display font-extrabold text-5xl text-parchment">{p.price}</span>
                <span className="text-mist text-sm">{p.unit}</span>
              </div>
              <p className="mt-3 text-sm text-mist">{p.desc}</p>
              <ul className="mt-7 space-y-2.5 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-center gap-2.5 text-sm text-parchment/85">
                    <Check className="w-4 h-4 text-terracotta shrink-0" strokeWidth={2.5} /> {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`plan-subscribe-${p.lookup_key}`}
                onClick={() => setSelected(p)}
                className={`mt-9 w-full py-3.5 rounded-full text-sm font-semibold transition-colors duration-300 ${
                  p.featured
                    ? "bg-terracotta text-parchment hover:bg-parchment hover:text-charcoal"
                    : "border border-white/15 text-parchment hover:bg-terracotta hover:border-terracotta"
                }`}
              >
                {p.lookup_key === "ondemand_bag" ? "Order a Pickup" : "Subscribe"}
              </button>
            </motion.div>
          ))}
        </div>
      </div>
      <CheckoutModal plan={selected} open={!!selected} onClose={() => setSelected(null)} />
    </section>
  );
}
